import {
  formatDuration,
  formatUsage,
  usageValue,
  cacheRate,
} from "../components/codex-usage";
import type {
  CodexStatistics,
  CodexUsageAggregate,
  UsageField,
} from "../api/client";
import "../components/overview-metrics";
import { TableColumns } from "../components/table-columns";
import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { api, type CodexRun, type TaskRecord } from "../api/client";
import { i18n } from "../i18n";
import { readable } from "../view-model";

const overviewTokenColumns = [
  { field: "input", label: "overviewInputTokens" },
  { field: "cached", label: "overviewInputCache" },
  { field: "output", label: "overviewOutputTokens" },
  { field: "reasoning_output", label: "usageReasoning" },
  { field: "total", label: "usageTotal" },
] as const;

interface CatalogCounts {
  repository: number;
  component: number;
  file: number;
  symbol: number;
  contract: number;
  evidence: number;
  behavior: number;
  consumer: number;
  contract_consumer: number;
  chunk: number;
  analysis_case: number;
}

@customElement("overview-view")
export class OverviewView extends LitElement {
  private readonly columns = new TableColumns(this);
  @state() private tasks: TaskRecord[] = [];
  @state() private keyCount = 0;
  @state() private catalogCounts: Partial<CatalogCounts> = {};
  @state() private skillCount = 0;
  @state() private recentRuns: CodexRun[] = [];
  @state() private loading = true;
  @state() private statistics: CodexStatistics | null = null;
  @state() private statisticsState: "loading" | "ready" | "error" = "loading";
  @state() private knowledgeState: "loading" | "ready" | "error" = "loading";
  @state() private runsState: "loading" | "ready" | "error" = "loading";
  @state() private error = "";

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    void this.load();
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private languageChanged = () => this.requestUpdate();

  private async load() {
    this.loading = true;
    this.knowledgeState = "loading";
    this.runsState = "loading";
    this.statisticsState = "loading";
    this.error = "";

    try {
      const results = await Promise.allSettled([
        api.tasks(),
        api.keys(),
        api.knowledgeCatalog(),
        api.skills(),
        api.codexRuns({
          page: 1,
          page_size: 5,
          is_availability_probe: "false",
        }),
        api.codexStatistics(),
      ]);
      const [tasks, keys, knowledge, skills, runs, statistics] = results;
      this.statisticsState =
        statistics.status === "fulfilled" ? "ready" : "error";
      if (statistics.status === "fulfilled") this.statistics = statistics.value;
      this.knowledgeState =
        knowledge.status === "fulfilled" ? "ready" : "error";
      this.runsState = runs.status === "fulfilled" ? "ready" : "error";
      if (tasks.status === "fulfilled") this.tasks = tasks.value.items || [];
      if (keys.status === "fulfilled")
        this.keyCount = keys.value.items?.length || 0;
      if (knowledge.status === "fulfilled")
        this.catalogCounts = knowledge.value.catalog.counts || {};
      if (skills.status === "fulfilled")
        this.skillCount = skills.value.count ?? skills.value.items?.length ?? 0;
      if (runs.status === "fulfilled") this.recentRuns = runs.value.items || [];
      const failed = results.find((result) => result.status === "rejected");
      if (failed && failed.status === "rejected")
        this.error = failed.reason?.message || "";
    } catch (error) {
      this.error = (error as Error).message;
      this.knowledgeState = "error";
      this.runsState = "error";
      this.statisticsState = "error";
    } finally {
      this.loading = false;
    }
  }

  private renderRecentJobs() {
    if (this.loading) {
      return html`<div aria-busy="true" aria-label=${i18n.t("overviewLoading")}>
        <a2k-progress></a2k-progress>
      </div>`;
    }

    if (this.error) {
      return html`
        <p class="status status-error">
          ${i18n.t("overviewLoadError", { message: this.error })}
        </p>
      `;
    }

    if (!this.tasks.length) {
      return html`<p class="empty-state">${i18n.t("overviewEmptyJobs")}</p>`;
    }

    return html`
      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>${i18n.t("overviewJob")}</th>
              <th>${i18n.t("overviewCommand")}</th>
              <th>${i18n.t("overviewState")}</th>
            </tr>
          </thead>
          <tbody>
            ${this.tasks.slice(0, 8).map(
              (task) => html`
                <tr>
                  <td>${readable(task.name)}</td>
                  <td>${readable(task.command)}</td>
                  <td>${readable(task.last_status)}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    `;
  }

  private renderRecentRuns() {
    if (this.loading) return html`<a2k-progress></a2k-progress>`;
    if (this.runsState === "error")
      return html`<p class="status status-error">
        ${i18n.t("overviewLoadError", { message: this.error })}
      </p>`;
    if (!this.recentRuns.length)
      return html`<p class="empty-state">${i18n.t("overviewEmptyRuns")}</p>`;
    return html`<div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>${i18n.t("overviewRunModel")}</th>
            <th>${i18n.t("overviewRunStatus")}</th>
            <th>${i18n.t("overviewRunTime")}</th>
            <th>${i18n.t("usageDuration")}</th>
            <th>${i18n.t("usageTotal")}</th>
          </tr>
        </thead>
        <tbody>
          ${this.recentRuns.map(
            (run) =>
              html`<tr>
                <td>${readable(run.model)}</td>
                <td>${readable(run.status)}</td>
                <td>${this.formatDate(run.created_at)}</td>
                <td>${formatDuration(run.latency_ms)}</td>
                <td>
                  ${formatUsage(usageValue(run.normalized_usage, "total"))}
                </td>
              </tr>`,
          )}
        </tbody>
      </table>
    </div>`;
  }

  private aggregateCell(
    value: CodexUsageAggregate,
    field: UsageField | "reasoning_output",
  ) {
    return html`<strong
      >${formatUsage(value.run_count === 0 ? 0 : value.tokens[field])}</strong
    >`;
  }

  private tokenColumn(
    value: CodexUsageAggregate,
    field: UsageField | "reasoning_output",
  ) {
    if (field !== "cached") return this.aggregateCell(value, field);
    const total = value.tokens.input;
    const cached = value.tokens.cached;
    const rate = cacheRate(total, cached);
    const rateText =
      rate == null
        ? "—"
        : `${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(rate)}%`;
    const countText =
      value.run_count === 0 ? "0" : cached == null ? "—" : formatUsage(cached);
    return html`<strong>${countText} / ${rateText}</strong>`;
  }

  private renderStatistics() {
    if (this.statisticsState === "loading")
      return html`<a2k-progress></a2k-progress>`;
    if (this.statisticsState === "error" || !this.statistics)
      return html`<p class="status status-error">
        ${i18n.t("overviewLoadError", { message: this.error })}
      </p>`;
    const data = this.statistics;
    return html`<p>${data.window.start_date} – ${data.window.end_date}</p>
      <p>${i18n.t("usageTimezone", { timezone: data.timezone })}</p>
      <div class="codex-week-summary">
        ${overviewTokenColumns.map(
          ({ field, label }) =>
            html`<div>
              <span>${i18n.t(label)}</span>
              ${this.tokenColumn(data.week, field)}
            </div>`,
        )}
        <div>
          <span>${i18n.t("usageAverage")}</span>
          <strong
            >${formatDuration(data.cumulative.average_duration_ms)}</strong
          >
        </div>
      </div>
      <p>${i18n.t("overviewCacheNote")}</p>
      <div class="table-wrap">
        <table class="data-table codex-daily-usage">
          <thead>
            <tr>
              <th>${i18n.t("usageDate")}</th>
              <th>${i18n.t("usageExecutions")}</th>
              ${overviewTokenColumns.map(({ label }) => html`<th>${i18n.t(label)}</th>`)}
            </tr>
          </thead>
          <tbody>
            ${data.daily.map(
              (day) =>
                html`<tr>
                  <td>${day.date}</td>
                  <td>${day.run_count}</td>
                  ${overviewTokenColumns.map(({ field }) => html`<td>${this.tokenColumn(day, field)}</td>`)}
                </tr>`,
            )}
          </tbody>
        </table>
      </div>
      ${
        data.coverage.history_before_backfill_may_be_missing
          ? html`<p>${i18n.t("usageHistoryNote")}</p>`
          : null
      }`;
  }

  private formatDate(value: string | undefined) {
    if (!value) return "-";
    try {
      return new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value));
    } catch {
      return value;
    }
  }

  render() {
    const activeTasks = this.tasks.filter((task) => task.enabled).length;
    const metrics = [
      [
        i18n.t("overviewRepositories"),
        String(this.catalogCounts.repository || 0),
      ],
      [i18n.t("overviewComponents"), String(this.catalogCounts.component || 0)],
      [i18n.t("overviewFiles"), String(this.catalogCounts.file || 0)],
      [i18n.t("overviewSymbols"), String(this.catalogCounts.symbol || 0)],
      [i18n.t("overviewContracts"), String(this.catalogCounts.contract || 0)],
      [i18n.t("overviewEvidence"), String(this.catalogCounts.evidence || 0)],
      [i18n.t("overviewBehaviors"), String(this.catalogCounts.behavior || 0)],
      [i18n.t("overviewConsumers"), String(this.catalogCounts.consumer || 0)],
      [i18n.t("overviewScheduledJobs"), String(this.tasks.length)],
      [i18n.t("overviewActiveJobs"), String(activeTasks)],
      [i18n.t("overviewSkills"), String(this.skillCount)],
      [i18n.t("overviewApiKeys"), String(this.keyCount)],
    ];

    return html`
      <page-heading
        heading=${i18n.t("overviewTitle")}
        description=${i18n.t("overviewDescription")}
      ></page-heading>
      <div class="metric-grid">
        ${metrics.map(
          ([label, value]) => html`
            <a2k-panel class="metric">
              <span>${label}</span>
              <strong>${value}</strong>
            </a2k-panel>
          `,
        )}
      </div>
      <overview-metrics
        .counts=${this.catalogCounts}
        .runs=${this.recentRuns}
        .language=${i18n.language}
        .knowledgeState=${this.knowledgeState}
        .runsState=${this.runsState}
      ></overview-metrics>
      <surface-window heading=${i18n.t("usageWeek")}>
        ${this.renderStatistics()}
      </surface-window>
      <div class="content-grid overview-panels">
        <surface-window heading=${i18n.t("overviewRecentJobs")}
          >${this.renderRecentJobs()}</surface-window
        >
        <surface-window heading=${i18n.t("overviewRecentRuns")}
          >${this.renderRecentRuns()}</surface-window
        >
      </div>
    `;
  }
}
